import useIsMobile from '../../hooks/useIsMobile';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';

// ── Defaults ────────────────────────────────────────────────────────────────────
const emptyCategory  = { name: '', name_en: '', sort_order: 0 };
const emptyType      = { budget_document_category_id: '', name: '', name_en: '', series: '', folio_sequence: 1, footer_text: '', footer_text_en: '', show_bank_accounts: true, auto_calculate_fees: false, sort_order: 0 };
const emptyConcCat   = { name: '', name_en: '', budget_bank_account_id: '' };
const emptyConcept   = { name: '', name_en: '', default_amount: 0, default_text_value: '', includes_iva: false, iva_rate: 16, type: 'normal', is_fees_concept: false };
const emptyAccount   = { label: '', label_en: '', bank: '', beneficiary: '', account_number: '', clabe: '', swift: '', branch: '', logo: null };

const conceptTypeConfig = {
  normal:      { label: 'Normal',      bg: '#EEF0FB', color: '#4F63D2' },
  informative: { label: 'Informativo', bg: '#F3F4F6', color: '#374151' },
  discount:    { label: 'Descuento',   bg: '#FEE2E2', color: '#991B1B' },
  general:     { label: 'General',     bg: '#EDE9FE', color: '#6D28D9' },
};

// ── Shared styles ───────────────────────────────────────────────────────────────
const inputSt  = { width: '100%', padding: '7px 10px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };
const smInput  = { ...inputSt, padding: '5px 8px', fontSize: 12 };
const selectSt = { ...inputSt, cursor: 'pointer' };
const smSelect = { ...smInput, cursor: 'pointer' };
const focusSt  = e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
const blurSt   = e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; };

const IconBtn = ({ onClick, title, variant = 'default', children }) => {
  const v = { default: ['#F4F6FB','#4B5563','#E5E7EB'], primary: ['#EEF0FB','#4F63D2','#dde1f7'], danger: ['#FEE2E2','#EF4444','#fecaca'] }[variant];
  return (
    <button onClick={onClick} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer', backgroundColor: v[0], color: v[1], flexShrink: 0 }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = v[2]}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = v[0]}>
      {children}
    </button>
  );
};

const Label = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
    {children} {required && <span style={{ color: '#EF4444' }}>*</span>}
  </label>
);

const EditPen = () => <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>;
const TrashCan = () => <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const PlusIcon = () => <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>;

const InlineForm = ({ title, onSubmit, onCancel, submitting, children }) => (
  <div style={{ background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB', padding: 12, marginBottom: 10 }}>
    <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 12, color: '#374151' }}>{title}</p>
    <form onSubmit={onSubmit}>
      {children}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button type="submit" disabled={submitting}
          style={{ padding: '5px 14px', borderRadius: 6, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}>
          {submitting ? '...' : 'Guardar'}
        </button>
        <button type="button" onClick={onCancel}
          style={{ padding: '5px 10px', borderRadius: 6, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#374151' }}>
          Cancelar
        </button>
      </div>
    </form>
  </div>
);

// ── Main component ───────────────────────────────────────────────────────────────
const BudgetDocumentTypes = () => {
  const isMobile = useIsMobile();
  const [docCategories, setDocCategories] = useState([]);
  const [docTypes, setDocTypes]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selectedType, setSelectedType]   = useState(null);
  const [submitting, setSubmitting]       = useState(false);

  // Doc categories
  const [showDocCatForm, setShowDocCatForm]   = useState(false);
  const [editingDocCat, setEditingDocCat]     = useState(null);
  const [docCatForm, setDocCatForm]           = useState(emptyCategory);

  // Doc types
  const [showTypeForm, setShowTypeForm]   = useState(false);
  const [editingType, setEditingType]     = useState(null);
  const [typeForm, setTypeForm]           = useState(emptyType);

  // Concept categories
  const [showConCatForm, setShowConCatForm]   = useState(false);
  const [editingConCat, setEditingConCat]     = useState(null);
  const [conCatForm, setConCatForm]           = useState(emptyConcCat);

  // Concepts
  const [showConceptForm, setShowConceptForm] = useState(null); // cat.id
  const [editingConcept, setEditingConcept]   = useState(null);
  const [conceptForm, setConceptForm]         = useState(emptyConcept);

  // Bank accounts
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount]   = useState(null);
  const [accountForm, setAccountForm]         = useState(emptyAccount);

  const fetchAll = async () => {
    try {
      const [catsRes, typesRes] = await Promise.all([api.get('/budget-document-categories'), api.get('/budget-document-types')]);
      setDocCategories(catsRes.data);
      setDocTypes(typesRes.data);
      if (selectedType) setSelectedType(typesRes.data.find(t => t.id === selectedType.id) || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const saveDocCat = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      editingDocCat ? await api.put(`/budget-document-categories/${editingDocCat.id}`, docCatForm) : await api.post('/budget-document-categories', docCatForm);
      toast.success(editingDocCat ? 'Categoría actualizada' : 'Categoría creada');
      setShowDocCatForm(false); setEditingDocCat(null); setDocCatForm(emptyCategory); fetchAll();
    } catch { toast.error('Error al guardar'); } finally { setSubmitting(false); }
  };

  const deleteDocCat = async (cat) => {
    if (!window.confirm(`¿Eliminar categoría "${cat.name}"?`)) return;
    try { await api.delete(`/budget-document-categories/${cat.id}`); toast.success('Eliminada'); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  const saveType = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      editingType ? await api.put(`/budget-document-types/${editingType.id}`, typeForm) : await api.post('/budget-document-types', typeForm);
      toast.success(editingType ? 'Tipo actualizado' : 'Tipo creado');
      setShowTypeForm(false); setEditingType(null); setTypeForm(emptyType); fetchAll();
    } catch { toast.error('Error al guardar'); } finally { setSubmitting(false); }
  };

  const deleteType = async (type) => {
    if (!window.confirm(`¿Eliminar tipo "${type.name}"?`)) return;
    try { await api.delete(`/budget-document-types/${type.id}`); toast.success('Eliminado'); if (selectedType?.id === type.id) setSelectedType(null); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  const saveConCat = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      editingConCat ? await api.put(`/budget-document-types/${selectedType.id}/concept-categories/${editingConCat.id}`, conCatForm) : await api.post(`/budget-document-types/${selectedType.id}/concept-categories`, conCatForm);
      toast.success(editingConCat ? 'Actualizada' : 'Creada');
      setShowConCatForm(false); setEditingConCat(null); setConCatForm(emptyConcCat); fetchAll();
    } catch { toast.error('Error al guardar'); } finally { setSubmitting(false); }
  };

  const deleteConCat = async (cat) => {
    if (!window.confirm(`¿Eliminar categoría "${cat.name}" y todos sus conceptos?`)) return;
    try { await api.delete(`/budget-document-types/${selectedType.id}/concept-categories/${cat.id}`); toast.success('Eliminada'); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  const saveConcept = async (e, categoryId) => {
    e.preventDefault(); setSubmitting(true);
    try {
      editingConcept ? await api.put(`/concept-categories/${categoryId}/concepts/${editingConcept.id}`, conceptForm) : await api.post(`/concept-categories/${categoryId}/concepts`, conceptForm);
      toast.success(editingConcept ? 'Actualizado' : 'Creado');
      setShowConceptForm(null); setEditingConcept(null); setConceptForm(emptyConcept); fetchAll();
    } catch { toast.error('Error al guardar'); } finally { setSubmitting(false); }
  };

  const deleteConcept = async (categoryId, concept) => {
    if (!window.confirm(`¿Eliminar concepto "${concept.name}"?`)) return;
    try { await api.delete(`/concept-categories/${categoryId}/concepts/${concept.id}`); toast.success('Eliminado'); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  const saveAccount = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(accountForm).forEach(([k, v]) => { if (k === 'logo') { if (v) fd.append('logo', v); } else { fd.append(k, v ?? ''); } });
      editingAccount
        ? await api.post(`/budget-document-types/${selectedType.id}/bank-accounts/${editingAccount.id}/update`, fd)
        : await api.post(`/budget-document-types/${selectedType.id}/bank-accounts`, fd);
      toast.success(editingAccount ? 'Cuenta actualizada' : 'Cuenta creada');
      setShowAccountForm(false); setEditingAccount(null); setAccountForm(emptyAccount); fetchAll();
    } catch { toast.error('Error al guardar'); } finally { setSubmitting(false); }
  };

  const deleteAccount = async (account) => {
    if (!window.confirm(`¿Eliminar cuenta "${account.label}"?`)) return;
    try { await api.delete(`/budget-document-types/${selectedType.id}/bank-accounts/${account.id}`); toast.success('Eliminada'); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
      <div style={{ width: 28, height: 28, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader title="Tipos de Documento" subtitle="Configura los tipos de presupuesto, conceptos y cuentas bancarias" />

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '300px 1fr', gap: 16, alignItems: 'start' }}>

        {/* ── Panel izquierdo: Categorías y tipos ──────────── */}
        <div>
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Categorías</span>
              <button onClick={() => { setShowDocCatForm(true); setEditingDocCat(null); setDocCatForm(emptyCategory); }}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 6, border: 'none', backgroundColor: '#EEF0FB', color: '#4F63D2', cursor: 'pointer' }}>
                <PlusIcon />
              </button>
            </div>

            {/* Formulario nueva/editar categoría */}
            {showDocCatForm && (
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#F9FAFB' }}>
                <InlineForm title={editingDocCat ? 'Editar Categoría' : 'Nueva Categoría'} onSubmit={saveDocCat} onCancel={() => { setShowDocCatForm(false); setEditingDocCat(null); }} submitting={submitting}>
                  <div style={{ marginBottom: 8 }}>
                    <Label required>Nombre ES</Label>
                    <input type="text" value={docCatForm.name} onChange={e => setDocCatForm({ ...docCatForm, name: e.target.value })} required style={smInput} onFocus={focusSt} onBlur={blurSt} />
                  </div>
                  <div>
                    <Label>Nombre EN</Label>
                    <input type="text" value={docCatForm.name_en} onChange={e => setDocCatForm({ ...docCatForm, name_en: e.target.value })} style={smInput} onFocus={focusSt} onBlur={blurSt} />
                  </div>
                </InlineForm>
              </div>
            )}

            {docCategories.length === 0 && !showDocCatForm && (
              <div style={{ padding: '16px', textAlign: 'center', fontSize: 12, color: '#9CA3AF' }}>Sin categorías</div>
            )}

            {docCategories.map(cat => (
              <div key={cat.id}>
                {/* Header categoría */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{cat.name}</span>
                  <div style={{ display: 'flex', gap: 3 }}>
                    <IconBtn onClick={() => { setEditingDocCat(cat); setDocCatForm({ name: cat.name, name_en: cat.name_en || '' }); setShowDocCatForm(true); }} title="Editar"><EditPen /></IconBtn>
                    <IconBtn onClick={() => deleteDocCat(cat)} title="Eliminar" variant="danger"><TrashCan /></IconBtn>
                  </div>
                </div>
                {/* Tipos dentro de esta categoría */}
                {docTypes.filter(t => t.budget_document_category_id === cat.id).map(type => (
                  <div key={type.id}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 14px', borderBottom: '1px solid #F9FAFB', cursor: 'pointer', backgroundColor: selectedType?.id === type.id ? '#EEF0FB' : '#fff', transition: 'background 0.1s' }}
                    onClick={() => setSelectedType(type)}
                    onMouseEnter={e => selectedType?.id !== type.id && (e.currentTarget.style.backgroundColor = '#F9FAFB')}
                    onMouseLeave={e => selectedType?.id !== type.id && (e.currentTarget.style.backgroundColor = '#fff')}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {type.series && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, backgroundColor: '#F3F4F6', color: '#374151' }}>{type.series}</span>}
                        <span style={{ fontSize: 13, fontWeight: selectedType?.id === type.id ? 700 : 500, color: selectedType?.id === type.id ? '#4F63D2' : '#374151' }}>{type.name}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      <IconBtn onClick={() => { setEditingType(type); setTypeForm({ budget_document_category_id: type.budget_document_category_id, name: type.name, name_en: type.name_en || '', series: type.series || '', folio_sequence: type.folio_sequence, footer_text: type.footer_text || '', footer_text_en: type.footer_text_en || '', show_bank_accounts: type.show_bank_accounts, auto_calculate_fees: type.auto_calculate_fees }); setShowTypeForm(true); }} title="Editar"><EditPen /></IconBtn>
                      <IconBtn onClick={() => deleteType(type)} title="Eliminar" variant="danger"><TrashCan /></IconBtn>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Botón nuevo tipo */}
          <button onClick={() => { setShowTypeForm(true); setEditingType(null); setTypeForm(emptyType); }}
            style={{ width: '100%', padding: '8px', borderRadius: 8, border: '1.5px dashed #C7D2F8', backgroundColor: '#F5F7FF', color: '#4F63D2', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <PlusIcon /> Nuevo Tipo de Documento
          </button>

          {/* Formulario nuevo/editar tipo */}
          {showTypeForm && (
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginTop: 10, overflow: 'hidden' }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 13, color: '#111827' }}>
                {editingType ? 'Editar Tipo' : 'Nuevo Tipo'}
              </div>
              <div style={{ padding: 14 }}>
                <form onSubmit={saveType}>
                  {[
                    { label: 'Categoría', el: <select value={typeForm.budget_document_category_id} onChange={e => setTypeForm({ ...typeForm, budget_document_category_id: e.target.value })} required style={smSelect} onFocus={focusSt} onBlur={blurSt}><option value="">Seleccionar...</option>{docCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>, required: true },
                    { label: 'Nombre ES', el: <input type="text" value={typeForm.name} onChange={e => setTypeForm({ ...typeForm, name: e.target.value })} required style={smInput} onFocus={focusSt} onBlur={blurSt} />, required: true },
                    { label: 'Nombre EN', el: <input type="text" value={typeForm.name_en} onChange={e => setTypeForm({ ...typeForm, name_en: e.target.value })} style={smInput} onFocus={focusSt} onBlur={blurSt} /> },
                  ].map(({ label, el, required }) => (
                    <div key={label} style={{ marginBottom: 10 }}><Label required={required}>{label}</Label>{el}</div>
                  ))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
                    <div style={{ marginBottom: 10 }}><Label>Serie</Label><input type="text" value={typeForm.series} onChange={e => setTypeForm({ ...typeForm, series: e.target.value })} placeholder="com" style={smInput} onFocus={focusSt} onBlur={blurSt} /></div>
                    <div style={{ marginBottom: 10 }}><Label>Folio inicial</Label><input type="number" min="1" value={typeForm.folio_sequence} onChange={e => setTypeForm({ ...typeForm, folio_sequence: e.target.value })} style={smInput} onFocus={focusSt} onBlur={blurSt} /></div>
                  </div>
                  <div style={{ marginBottom: 10 }}><Label>Pie de página ES</Label><textarea rows={2} value={typeForm.footer_text} onChange={e => setTypeForm({ ...typeForm, footer_text: e.target.value })} style={{ ...smInput, resize: 'vertical' }} onFocus={focusSt} onBlur={blurSt} /></div>
                  <div style={{ marginBottom: 10 }}><Label>Pie de página EN</Label><textarea rows={2} value={typeForm.footer_text_en} onChange={e => setTypeForm({ ...typeForm, footer_text_en: e.target.value })} style={{ ...smInput, resize: 'vertical' }} onFocus={focusSt} onBlur={blurSt} /></div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4B5563', cursor: 'pointer', marginBottom: 8 }}>
                    <input type="checkbox" checked={typeForm.show_bank_accounts} onChange={e => setTypeForm({ ...typeForm, show_bank_accounts: e.target.checked })} style={{ accentColor: '#4F63D2' }} />
                    Mostrar cuentas bancarias en PDF
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4B5563', cursor: 'pointer', marginBottom: 12 }}>
                    <input type="checkbox" checked={typeForm.auto_calculate_fees} onChange={e => setTypeForm({ ...typeForm, auto_calculate_fees: e.target.checked })} style={{ accentColor: '#4F63D2' }} />
                    Calcular Honorarios automáticamente con el mayor Valor Informativo
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="submit" disabled={submitting} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}>{submitting ? '...' : 'Guardar'}</button>
                    <button type="button" onClick={() => { setShowTypeForm(false); setEditingType(null); }} style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#374151' }}>Cancelar</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* ── Panel derecho: Conceptos y cuentas ──────────── */}
        {!selectedType ? (
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <EmptyState icon="📋" title="Selecciona un tipo de documento" subtitle="Haz clic en un tipo para configurar sus conceptos y cuentas bancarias" />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* ── Categorías de conceptos ─── */}
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{selectedType.name}</span>
                  <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 8 }}>— Categorías de Conceptos</span>
                </div>
                <button onClick={() => { setShowConCatForm(true); setEditingConCat(null); setConCatForm(emptyConcCat); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, backgroundColor: '#4F63D2', color: '#fff', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                  <PlusIcon /> Nueva Categoría
                </button>
              </div>
              <div style={{ padding: 14 }}>
                {/* Form nueva/editar categoría de conceptos */}
                {showConCatForm && (
                  <InlineForm title={editingConCat ? 'Editar Categoría' : 'Nueva Categoría de Conceptos'} onSubmit={saveConCat} onCancel={() => { setShowConCatForm(false); setEditingConCat(null); }} submitting={submitting}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px', marginBottom: 8 }}>
                      <div><Label required>Nombre ES</Label><input type="text" value={conCatForm.name} onChange={e => setConCatForm({ ...conCatForm, name: e.target.value })} required placeholder="Impuestos y derechos" style={smInput} onFocus={focusSt} onBlur={blurSt} /></div>
                      <div><Label>Nombre EN</Label><input type="text" value={conCatForm.name_en} onChange={e => setConCatForm({ ...conCatForm, name_en: e.target.value })} placeholder="Taxes and fees" style={smInput} onFocus={focusSt} onBlur={blurSt} /></div>
                    </div>
                    <div>
                      <Label>Cuenta bancaria asociada</Label>
                      <select value={conCatForm.budget_bank_account_id || ''} onChange={e => setConCatForm({ ...conCatForm, budget_bank_account_id: e.target.value })} style={smSelect} onFocus={focusSt} onBlur={blurSt}>
                        <option value="">Sin cuenta asociada</option>
                        {selectedType?.bank_accounts?.map(acc => <option key={acc.id} value={acc.id}>{acc.label}</option>)}
                      </select>
                      {(!selectedType?.bank_accounts?.length) && <p style={{ margin: '3px 0 0', fontSize: 11, color: '#F59E0B' }}>⚠ Primero agrega cuentas bancarias</p>}
                    </div>
                  </InlineForm>
                )}

                {!selectedType.concept_categories?.length && !showConCatForm && (
                  <EmptyState icon="📂" title="Sin categorías de conceptos" subtitle="Agrega categorías para organizar los conceptos del presupuesto" />
                )}

                {selectedType.concept_categories?.map(cat => (
                  <div key={cat.id} style={{ border: '1px solid #E5E7EB', borderRadius: 9, marginBottom: 12, overflow: 'hidden' }}>
                    {/* Header cat conceptos */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{cat.name}</span>
                        {cat.name_en && <span style={{ fontSize: 11, color: '#9CA3AF' }}>/ {cat.name_en}</span>}
                        {cat.budget_bank_account_id && (
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20, backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>
                            {selectedType.bank_accounts?.find(a => a.id === cat.budget_bank_account_id)?.label || 'Cuenta asociada'}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 3 }}>
                        <IconBtn onClick={() => { setEditingConCat(cat); setConCatForm({ name: cat.name, name_en: cat.name_en || '', budget_bank_account_id: cat.budget_bank_account_id || '' }); setShowConCatForm(true); }} title="Editar"><EditPen /></IconBtn>
                        <IconBtn onClick={() => { setShowConceptForm(cat.id); setEditingConcept(null); setConceptForm(emptyConcept); }} title="Agregar concepto" variant="primary"><PlusIcon /></IconBtn>
                        <IconBtn onClick={() => deleteConCat(cat)} title="Eliminar categoría" variant="danger"><TrashCan /></IconBtn>
                      </div>
                    </div>

                    {/* Form nuevo/editar concepto */}
                    {showConceptForm === cat.id && (
                      <div style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#F9FAFB' }}>
                        <InlineForm title={editingConcept ? 'Editar Concepto' : 'Nuevo Concepto'} onSubmit={e => saveConcept(e, cat.id)} onCancel={() => { setShowConceptForm(null); setEditingConcept(null); }} submitting={submitting}>
                          <div style={{ display: 'grid', gridTemplateColumns: conceptForm.type === 'general' ? '2fr 2fr 2fr 1fr' : '2fr 2fr 1fr 1fr', gap: '0 8px', marginBottom: 8 }}>
                            <div><Label required>Nombre ES</Label><input type="text" value={conceptForm.name} onChange={e => setConceptForm({ ...conceptForm, name: e.target.value })} required style={smInput} onFocus={focusSt} onBlur={blurSt} /></div>
                            <div><Label>Nombre EN</Label><input type="text" value={conceptForm.name_en} onChange={e => setConceptForm({ ...conceptForm, name_en: e.target.value })} style={smInput} onFocus={focusSt} onBlur={blurSt} /></div>
                            {conceptForm.type === 'general' ? (
                              <div><Label>Valor por defecto</Label><input type="text" value={conceptForm.default_text_value} onChange={e => setConceptForm({ ...conceptForm, default_text_value: e.target.value })} placeholder="Se llena por presupuesto" style={smInput} onFocus={focusSt} onBlur={blurSt} /></div>
                            ) : (
                              <div><Label>Monto</Label><input type="number" min="0" step="0.01" value={conceptForm.default_amount} onChange={e => setConceptForm({ ...conceptForm, default_amount: e.target.value })} style={smInput} onFocus={focusSt} onBlur={blurSt} /></div>
                            )}
                            <div><Label>Tipo</Label><select value={conceptForm.type} onChange={e => setConceptForm({ ...conceptForm, type: e.target.value })} style={smSelect} onFocus={focusSt} onBlur={blurSt}><option value="normal">Normal</option><option value="informative">Informativo</option><option value="discount">Descuento</option><option value="general">General</option></select></div>
                          </div>
                          {conceptForm.type !== 'general' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', color: '#4B5563' }}>
                                <input type="checkbox" checked={conceptForm.includes_iva} onChange={e => setConceptForm({ ...conceptForm, includes_iva: e.target.checked })} style={{ accentColor: '#4F63D2' }} /> Incluye IVA
                              </label>
                              {conceptForm.includes_iva && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <Label>Tasa IVA %</Label>
                                  <input type="number" min="0" max="100" step="0.01" value={conceptForm.iva_rate} onChange={e => setConceptForm({ ...conceptForm, iva_rate: e.target.value })} style={{ ...smInput, width: 70 }} onFocus={focusSt} onBlur={blurSt} />
                                </div>
                              )}
                              {conceptForm.type === 'normal' && (
                                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', color: '#4B5563' }}>
                                  <input type="checkbox" checked={conceptForm.is_fees_concept} onChange={e => setConceptForm({ ...conceptForm, is_fees_concept: e.target.checked })} style={{ accentColor: '#4F63D2' }} />
                                  Recibe el valor mayor de los Valores Informativos
                                </label>
                              )}
                            </div>
                          )}
                        </InlineForm>
                      </div>
                    )}

                    {/* Lista de conceptos */}
                    {!cat.concepts?.length && showConceptForm !== cat.id ? (
                      <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Sin conceptos</div>
                    ) : (
                      cat.concepts?.map(concept => {
                        const cc = conceptTypeConfig[concept.type] || conceptTypeConfig.normal;
                        return (
                          <div key={concept.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #F9FAFB' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 20, backgroundColor: cc.bg, color: cc.color }}>{cc.label}</span>
                                <span style={{ fontSize: 13, color: '#374151' }}>{concept.name}</span>
                                {concept.name_en && <span style={{ fontSize: 11, color: '#9CA3AF' }}>/ {concept.name_en}</span>}
                                {concept.includes_iva && <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 20, backgroundColor: '#FEF3C7', color: '#92400E' }}>+IVA {concept.iva_rate}%</span>}
                                {concept.is_fees_concept && <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 20, backgroundColor: '#D1FAE5', color: '#065F46' }}>🧮 Auto (valor mayor)</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              {concept.type === 'general' ? (
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#6D28D9', fontStyle: concept.default_text_value ? 'normal' : 'italic' }}>
                                  {concept.default_text_value || 'se llena por presupuesto'}
                                </span>
                              ) : (
                                <span style={{ fontSize: 12, fontWeight: 700, color: concept.type === 'discount' ? '#EF4444' : '#374151' }}>
                                  ${Number(concept.default_amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </span>
                              )}
                              <IconBtn onClick={() => { setEditingConcept(concept); setConceptForm({ name: concept.name, name_en: concept.name_en || '', default_amount: concept.default_amount, default_text_value: concept.default_text_value || '', includes_iva: concept.includes_iva, iva_rate: concept.iva_rate, type: concept.type, is_fees_concept: concept.is_fees_concept || false }); setShowConceptForm(cat.id); }} title="Editar"><EditPen /></IconBtn>
                              <IconBtn onClick={() => deleteConcept(cat.id, concept)} title="Eliminar" variant="danger"><TrashCan /></IconBtn>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Cuentas bancarias ─── */}
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Cuentas Bancarias</span>
                <button onClick={() => { setShowAccountForm(true); setEditingAccount(null); setAccountForm(emptyAccount); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, backgroundColor: '#4F63D2', color: '#fff', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                  <PlusIcon /> Agregar
                </button>
              </div>
              <div style={{ padding: 14 }}>
                {/* Form nueva/editar cuenta */}
                {showAccountForm && (
                  <InlineForm title={editingAccount ? 'Editar Cuenta' : 'Nueva Cuenta Bancaria'} onSubmit={saveAccount} onCancel={() => { setShowAccountForm(false); setEditingAccount(null); }} submitting={submitting}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0 12px' }}>
                      {[
                        { label: 'Etiqueta ES', field: 'label', required: true, placeholder: 'Honorarios Notariales' },
                        { label: 'Etiqueta EN', field: 'label_en', placeholder: 'Notarial Fees' },
                        { label: 'Banco', field: 'bank', required: true, placeholder: 'BBVA' },
                        { label: 'Beneficiario', field: 'beneficiary', required: true },
                        { label: 'Número de cuenta', field: 'account_number', required: true },
                        { label: 'CLABE', field: 'clabe' },
                        { label: 'SWIFT', field: 'swift' },
                        { label: 'Sucursal', field: 'branch' },
                      ].map(({ label, field, required, placeholder }) => (
                        <div key={field} style={{ marginBottom: 8 }}>
                          <Label required={required}>{label}</Label>
                          <input type="text" value={accountForm[field] || ''} onChange={e => setAccountForm({ ...accountForm, [field]: e.target.value })} required={required} placeholder={placeholder || ''} style={smInput} onFocus={focusSt} onBlur={blurSt} />
                        </div>
                      ))}
                      <div style={{ gridColumn: '1 / -1', marginBottom: 8 }}>
                        <Label>Logo del banco</Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {editingAccount?.logo_url && !accountForm.logo && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <img src={editingAccount.logo_url} alt="Logo actual" style={{ height: 32, objectFit: 'contain', border: '1px solid #E5E7EB', borderRadius: 4, padding: 3, background: '#fff' }} />
                              <button type="button" onClick={async () => { await api.put(`/budget-document-types/${selectedType.id}/bank-accounts/${editingAccount.id}`, { remove_logo: true }); fetchAll(); setEditingAccount(p => ({ ...p, logo_url: null })); }}
                                style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, border: '1px solid #FECACA', backgroundColor: '#FEF2F2', color: '#991B1B', cursor: 'pointer' }}>
                                Quitar logo
                              </button>
                            </div>
                          )}
                          <input type="file" accept="image/*" onChange={e => setAccountForm({ ...accountForm, logo: e.target.files[0] || null })} style={{ fontSize: 12 }} />
                        </div>
                        <p style={{ margin: '3px 0 0', fontSize: 11, color: '#9CA3AF' }}>PNG o JPG, máximo 2MB. Se mostrará en el PDF del presupuesto.</p>
                      </div>
                    </div>
                  </InlineForm>
                )}

                {!selectedType.bank_accounts?.length && !showAccountForm && (
                  <EmptyState icon="🏦" title="Sin cuentas bancarias" subtitle="Agrega cuentas para mostrarlas en los presupuestos" />
                )}

                {selectedType.bank_accounts?.map(account => (
                  <div key={account.id} style={{ border: '1px solid #E5E7EB', borderRadius: 9, padding: '12px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 0 }}>
                      {account.logo_url && (
                        <img src={account.logo_url} alt={account.bank} style={{ height: 36, objectFit: 'contain', border: '1px solid #E5E7EB', borderRadius: 6, padding: 4, backgroundColor: '#fff', flexShrink: 0 }} />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{account.label}</span>
                          {account.label_en && <span style={{ fontSize: 11, color: '#9CA3AF' }}>/ {account.label_en}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 2 }}><strong>{account.bank}</strong> — {account.beneficiary}</div>
                        <div style={{ fontSize: 12, color: '#9CA3AF' }}>
                          Cuenta: {account.account_number}
                          {account.clabe && ` · CLABE: ${account.clabe}`}
                          {account.branch && ` · Suc: ${account.branch}`}
                        </div>
                        {selectedType.concept_categories?.filter(c => c.budget_bank_account_id === account.id).map(c => (
                          <span key={c.id} style={{ display: 'inline-block', marginTop: 4, marginRight: 4, fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20, backgroundColor: '#EEF0FB', color: '#4F63D2' }}>{c.name}</span>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <IconBtn onClick={() => { setEditingAccount(account); setAccountForm({ label: account.label, label_en: account.label_en || '', bank: account.bank, beneficiary: account.beneficiary, account_number: account.account_number, clabe: account.clabe || '', swift: account.swift || '', branch: account.branch || '', logo: null }); setShowAccountForm(true); }} title="Editar"><EditPen /></IconBtn>
                      <IconBtn onClick={() => deleteAccount(account)} title="Eliminar" variant="danger"><TrashCan /></IconBtn>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetDocumentTypes;
